import { gql } from "@apollo/client";

// Lives under `WebAPI/` rather than `Log/Revision/` so Drive's codegen `documents`
// glob — which only excludes `WebAPI/**` — doesn't try to validate these operations
// against the Drive schema. Types below are hand-written rather than generated:
// `npm run generate` needs a live schema with SC-555174's fields deployed, which
// isn't the case yet (see .work-os.md).

export const RevisionSuggestionFields = `
  revisionSuggestionId
  status
  exceptionType
  startTime
  endTime
  location
  distance
  notes
  jobId
  workOrderId
  workCodeIds
`;

export type RevisionSuggestionFragment = {
  readonly revisionSuggestionId: string;
  readonly status: "OFF" | "ON" | "D" | "SB";
  readonly exceptionType: string | null;
  readonly startTime: string;
  readonly endTime: string;
  readonly location: string | null;
  readonly distance: number | null;
  readonly notes: string;
  readonly jobId: number | null;
  readonly workOrderId: number | null;
  readonly workCodeIds: readonly number[] | null;
};

export const CreateRevisionSuggestionDocument = gql`
  mutation CreateRevisionSuggestion($driverId: Int!, $input: CreateRevisionSuggestionInput!) {
    createRevisionSuggestion(driverId: $driverId, input: $input) {
      ${RevisionSuggestionFields}
    }
  }
`;

export type CreateRevisionSuggestionInput = {
  // Matches `Driver.Status.Actual.Actual` (`"ON" | "OFF" | "D" | "SB"`) rather than the
  // backend's documented "OFF, ON, or SB" — the REST path never restricted this client-side
  // either, so this keeps parity rather than adding a new client-side validation.
  readonly status: "OFF" | "ON" | "D" | "SB";
  readonly location: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly notes: string;
  // Deliberately omitted for now: REST's `exception_type` (YM/PU) and this input's
  // `annotationType` (UC/EC/ADC) are not a confirmed 1:1 mapping — see SC-508345 and
  // this MR's description. Wire it up once backend confirms the mapping.
  readonly jobId?: number;
  readonly workOrderId?: number;
  readonly workCodeIds?: readonly number[];
};

export type CreateRevisionSuggestionVariables = {
  readonly driverId: number;
  readonly input: CreateRevisionSuggestionInput;
};

export type CreateRevisionSuggestionResult = {
  readonly createRevisionSuggestion: RevisionSuggestionFragment;
};

export const SuggestedRevisionsDocument = gql`
  query SuggestedRevisions($driverId: Int) {
    suggestedRevisions(driverId: $driverId) {
      ${RevisionSuggestionFields}
    }
  }
`;

export type SuggestedRevisionsVariables = {
  readonly driverId?: number;
};

export type SuggestedRevisionsResult = {
  readonly suggestedRevisions: readonly RevisionSuggestionFragment[];
};

export const UpdateRevisionSuggestionDocument = gql`
  mutation UpdateRevisionSuggestionStatus($suggestionId: String!, $status: String!) {
    updateRevisionSuggestion(suggestionId: $suggestionId, status: $status) {
      success
    }
  }
`;

export type UpdateRevisionSuggestionVariables = {
  readonly suggestionId: string;
  readonly status: "cancelled";
};

export type UpdateRevisionSuggestionResult = {
  readonly updateRevisionSuggestion: { readonly success: boolean };
};
